import "./RichTextEditorComponent.scss";
import React, {useCallback, useMemo, useState} from "react";
import {Slate, Editable, useSlate, withReact} from "slate-react";
import {createEditor, Editor, Transforms, Element as SlateElement} from "slate";
// import {withHistory} from "slate-history";
import IconButton from "@mui/material/IconButton";
import isHotkey from "is-hotkey";
import {ImageConfig} from "../../../constants";
import {FormHelperText} from "@mui/material";

interface RichTextEditorComponentProps {
    value: any;
    onChange?: (value: string) => void;
    readOnly?: boolean;
    errorMessage?: any;
    label?: string;
    required?: boolean;
}

interface CustomElement {
    type: 'paragraph' | 'numbered-list' | 'bulleted-list' | 'list-item';
    children: any[];
}

interface CustomText {
    text: string;
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
}

interface ElementProps {
    attributes: any;
    children: any;
    element: CustomElement;
}

interface LeafProps {
    attributes: any;
    children: any;
    leaf: CustomText;
}

const IconConfiguration: Record<string, JSX.Element> = {
    bold: <ImageConfig.BoldIcon/>,
    italic: <ImageConfig.ItalicIcon/>,
    underline: <ImageConfig.UnderlineIcon/>,
    orderedList: <ImageConfig.OrderedListIcon/>,
    unorderedList: <ImageConfig.UnorderedListIcon/>,
};

const HOTKEYS: Record<string, string> = {
    'mod+b': 'bold',
    'mod+i': 'italic',
    'mod+u': 'underline',
};

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

const isBlockActive = (editor: Editor, format: string, blockType: string = 'type') => {
    const {selection} = editor;
    if (!selection) return false;
    const [match] = Array.from(
        Editor.nodes(editor, {
            at: Editor.unhangRange(editor, selection),
            match: n =>
                // @ts-ignore
                !Editor.isEditor(n) && SlateElement.isElement(n) && n[blockType] === format,
        })
    );
    return !!match;
};

const isMarkActive = (editor: Editor, format: string) => {
    const marks: any = Editor.marks(editor);
    return marks ? marks[format] === true : false;
};

const toggleBlock = (editor: Editor, format: string) => {
    const isActive = isBlockActive(editor, format, TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type');
    const isList = LIST_TYPES.includes(format);
    Transforms.unwrapNodes(editor, {
        match: n =>
            !Editor.isEditor(n) &&
            SlateElement.isElement(n) &&
            // @ts-ignore
            LIST_TYPES.includes(n.type) &&
            !TEXT_ALIGN_TYPES.includes(format),
        split: true,
    });
    const newProperties: Partial<any> = TEXT_ALIGN_TYPES.includes(format)
        ? {align: isActive ? undefined : format}
        : {type: isActive ? 'paragraph' : isList ? 'list-item' : format};
    Transforms.setNodes(editor, newProperties);
    if (!isActive && isList) {
        const block = {type: format, children: []};
        Transforms.wrapNodes(editor, block);
    }
};

const toggleMark = (editor: Editor, format: string) => {
    const isActive = isMarkActive(editor, format);
    isActive ? Editor.removeMark(editor, format) : Editor.addMark(editor, format, true);
};

const MarkButton: React.FC<{ format: string; icon: string }> = ({format, icon}) => {
    const editor = useSlate();
    return (
        <IconButton
            // color="primary"
            sx={{opacity: isMarkActive(editor, format) ? 1 : 0.4, color: "000"}}
            size="small"
            onMouseDown={(event) => {
                event.preventDefault();
                toggleMark(editor, format);
            }}
        >
            {IconConfiguration[icon]}
        </IconButton>
    );
};

const BlockButton: React.FC<{ format: string; icon: string }> = ({format, icon}) => {
    const editor = useSlate();
    return (
        <IconButton
            // color="primary"
            sx={{opacity: isBlockActive(editor, format) ? 1 : 0.4, color: "000"}}
            size="small"
            onMouseDown={(event) => {
                event.preventDefault();
                toggleBlock(editor, format);
            }}
        >
            {IconConfiguration[icon]}
        </IconButton>
    );
};

const Element: React.FC<ElementProps> = ({attributes, children, element}) => {
    switch (element.type) {
        case 'bulleted-list':
            return <ul {...attributes}>{children}</ul>;
        case 'list-item':
            return <li {...attributes}>{children}</li>;
        case 'numbered-list':
            return <ol {...attributes}>{children}</ol>;
        default:
            return <p {...attributes}>{children}</p>;
    }
};

const Leaf: React.FC<LeafProps> = ({attributes, children, leaf}) => {
    if (leaf.bold) children = <strong>{children}</strong>;
    if (leaf.italic) children = <em>{children}</em>;
    if (leaf.underline) children = <u>{children}</u>;
    return <span {...attributes}>{children}</span>;
};

const htmlTransformer = (value: any[]): string => {
    let template = "<div>";

    const generateTag = (text: string, bold?: boolean, italic?: boolean, underline?: boolean): string => {
        let result = '';
        if (bold) result += '<strong>';
        if (italic) result += '<em>';
        if (underline) result += '<u>';
        result += text;
        if (underline) result += '</u>';
        if (italic) result += '</em>';
        if (bold) result += '</strong>';
        return result;
    };

    const generateList = (items: any[], type: string): string => {
        let listTemplate = type === 'bulleted-list' ? '<ul>' : '<ol>';
        for (const item of items) {
            listTemplate += '<li>';
            for (const child of item.children) {
                listTemplate += generateTag(child.text, child.bold, child.italic, child.underline);
            }
            listTemplate += '</li>';
        }
        listTemplate += type === 'bulleted-list' ? '</ul>' : '</ol>';
        return listTemplate;
    };

    for (const currentNode of value) {
        const childrens = currentNode.children;
        // if (currentNode.type === "heading-one") {
        //     template += "<h5>";
        //     for (const child of childrens) {
        //         template += generateTag(child.text, child.bold, child.italic, child.underline);
        //     }
        //     template += "</h5>";
        // }
        if (currentNode.type === "bulleted-list" || currentNode.type === "numbered-list") {
            template += generateList(childrens, currentNode.type);
        } else { //consider as paragraph
            template += "<p>";
            for (const child of childrens) {
                template += generateTag(child.text, child.bold, child.italic, child.underline);
            }
            template += "</p>";
        }
    }
    template += "</div>";
    return template;
};

const reverseTransformer = (html: string): any[] => {
    const parseHtml = (html: string): HTMLElement => {
        const parser = new DOMParser();
        return parser.parseFromString(html, 'text/html').body;
    };

    const parseText = (node: HTMLElement): CustomText => {
        const text = node?.textContent || "";
        const bold = node?.tagName === 'STRONG' || node?.closest('strong') !== null || node?.childNodes?.[0].nodeName === "STRONG";
        const italic = node?.tagName === 'EM' || node?.tagName === 'I' || node?.closest('em') !== null || node?.closest('i') !== null || node?.childNodes?.[0]?.nodeName === "EM" || node?.childNodes?.[0]?.nodeName === "I";
        const underline = node?.tagName === 'U' || node?.closest('u') !== null || node?.childNodes?.[0]?.nodeName === "U";
        return {text, bold, italic, underline};
    };

    const parseChildren = (nodes: NodeList): (CustomText | CustomElement)[] => {
        const children: any[] = [];
        nodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                children.push({text: node.textContent || ''});
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                children.push(parseText(node as HTMLElement));
            }
        });
        return children.length > 0 ? children : [{text: ""}];
    };

    const doc = parseHtml(html);
    const config: any[] = [];
    const wrapper = doc.querySelector('div');
    if (wrapper && wrapper.childNodes.length > 0) {
        wrapper.childNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as HTMLElement;
                // if (element.tagName === 'H5') {
                //     config.push({
                //         type: 'heading-one',
                //         children: parseChildren(element.childNodes),
                //     });
                // }
                if (element.tagName === 'P') {
                    config.push({
                        type: 'paragraph',
                        children: parseChildren(element.childNodes),
                    });
                } else if (element.tagName === 'OL') {
                    const listItems: (CustomElement | CustomText)[] = [];
                    element.querySelectorAll('li').forEach((li) => {
                        listItems.push({
                            type: 'list-item',
                            children: parseChildren(li.childNodes),
                        });
                    });
                    config.push({
                        type: 'numbered-list',
                        children: listItems,
                    });
                } else if (element.tagName === 'UL') {
                    const listItems: CustomElement[] = [];
                    element.querySelectorAll('li').forEach((li) => {
                        listItems.push({
                            type: 'list-item',
                            children: parseChildren(li.childNodes),
                        });
                    });
                    config.push({
                        type: 'bulleted-list',
                        children: listItems,
                    });
                }
            }
        });
    } else {
        config.push({
            type: "paragraph",
            children: [{text: ""}]
        })
    }
    return config;
};
// const config = reverseTransformer("<div><p><strong>Methodology: </strong>This Urinary TractInfection (UTI) molecular multiple pathogen detection testis as a Laboratory Developed Test(LDT). Utilizing multiplex realtime PCR (qPCR), it specifically identifies pathogens (as listed above) presentin the patient's urine specimen. The patient's urine specimen undergoes treatmentfor RNA/DNAextraction and is then processed to detectthe presence of various urinary pathogens outlined in the panel (refer to above list). This process employs specific primers/probes on a realtimePCR instrument. The results are compared to contrived positive controls, internal controls, and NegativeTemplateControls (QualityControl Samples) run alongside the patient's extracted urine sample or the molecular diagnosis of urinary infections.</p><ul><li>list1</li></ul><ul><li>list2</li></ul><ul><li>list3</li></ul><p><strong>Limitations: </strong>All molecular tests have limitations. If a patient receives a 'NOT DETECTED' or 'NEGATIVE' result, itmeans thatthe pathogens mentioned in thereportwere notdetected in the specimen atthe time of sample collection. However, it's importantto note thatthis resultdoes not rule outthe possibility thatthe patient's symptoms, experienced prior to this test, may be caused by pathogens notincluded in this panel.</p><ol><li> list1</li><li> list2</li><li>list3</li></ol><p><strong>Eligibility for Testing:</strong>This test should be used for diagnostic use only and is limited to patients suspected of urinary infections by their healthcare provider. The eligibility determination ultimately rests on the clinician’s judgment. </p><p><strong>Conduct oftheTest</strong>:This testis performed under strict compliance and guidelines ofAdvanced Genomics LLC, including the instruments, reagents, and other recommended procedures. This includes the safety protocolswhere all laboratory personnel are appropriately trained in qPCR techniques and use appropriate laboratory and personal protective equipmentwhen handling this kit/test and use this testin accordancewith the authorized labeling. </p><p><strong>Result Reports for HealthcareProviders and Patients: </strong>We have a process for reporting test results to healthcare providers as appropriate. Result reportswill be provided to healthcare providers and patients.</p><p><strong>PerformanceData and Reporting: </strong>We collectinformation on the performance ofthe test and report any suspected occurrence offalse positive or false negative results and significant deviations from the established performance characteristics ofthe testof which we become aware to concerned authorities.</p><p><strong>Recordkeeping: </strong>As an authorized laboratory we ensure all records associated with this test are maintained until otherwise notified. Such records are available to</p><p></p></div>")
// console.log(config, "this is the config")

function countLines(text: string, maxCharsPerLine: number): number {
    return Math.max(1, Math.ceil(text.length / maxCharsPerLine));
}

export function isMaximumLineCountReached(htmlContent: string, maxLines: number = 32, maxCharsPerLine: number = 145): boolean {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    let totalLines = 0;

    const elements = doc.querySelectorAll('p, ol, li');
    elements.forEach(element => {
        const text = element.textContent || '';
        const lines = countLines(text, maxCharsPerLine);
        totalLines += lines;
    });
    return totalLines <= maxLines;
}

export function isEmptyHtml(htmlContent: string): boolean {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');

    const pElements = doc.querySelector('p');
    const liElements = doc.querySelector('li');

    const hasTextContent = (elements: Element | null): boolean => {
        //check if text is available inside element
        if (elements) {
            const text = elements.textContent || '';
            //is empty text?
            if (text.trim().length > 0) {
                return false;
            }
        }
        return true;
    }

    return hasTextContent(pElements) && hasTextContent(liElements);
}

// const htmlContent: string = `
// <div><p> <strong>Methodology Test: </strong>This Urinary TractInfection (UTI) molecular multiple pathogen detection testis as a Laboratory Developed Test(LDT). Utilizing multiplex realtime PCR (qPCR), it specifically identifies pathogens (as listed above) presentin the patient's urine specimen. The patient's urine specimen undergoes treatmentfor RNA/DNAextraction and is then processed to detectthe presence of various urinary pathogens outlined in the panel (refer to above list). This process employs specific primers/probes on a realtimePCR instrument. The results are compared to contrived positive controls, internal controls, and NegativeTemplateControls (QualityControl Samples) run alongside the patient's extracted urine sample or the molecular diagnosis of urinary infections. </p><ul><li>list1</li></ul><ul><li>list2</li></ul><ul><li>list3</li></ul><p> <strong>Limitations: </strong>All molecular tests have limitations. If a patient receives a 'NOT DETECTED' or 'NEGATIVE' result, itmeans thatthe pathogens mentioned in thereportwere notdetected in the specimen atthe time of sample collection. However, it's importantto note thatthis resultdoes not rule outthe possibility thatthe patient's symptoms, experienced prior to this test, may be caused by pathogens notincluded in this panel. </p><ol><li>list1</li><li>list2</li><li>list3</li></ol><p> <strong>Eligibility for Testing:</strong>This test should be used for diagnostic use only and is limited to patients suspected of urinary infections by their healthcare provider. The eligibility determination ultimately rests on the clinician’s judgment. </p><p> <strong>Conduct oftheTest</strong>:This testis performed under strict compliance and guidelines ofAdvanced Genomics LLC, including the instruments, reagents, and other recommended procedures. This includes the safety protocolswhere all laboratory personnel are appropriately trained in qPCR techniques and use appropriate laboratory and personal protective equipmentwhen handling this kit/test and use this testin accordancewith the authorized labeling. </p><p> <strong>Result Reports for HealthcareProviders and Patients: </strong>We have a process for reporting test results to healthcare providers as appropriate. Result reportswill be provided to healthcare providers and patients. </p><p> <strong>PerformanceData and Reporting: </strong>We collectinformation on the performance ofthe test and report any suspected occurrence offalse positive or false negative results and significant deviations from the established performance characteristics ofthe testof which we become aware to concerned authorities. </p><p> <strong>Recordkeeping: </strong>As an authorized laboratory we ensure all records associated with this test are maintained until otherwise notified. Such records are available to </p></div>
// `;

// console.log(validateHTML(htmlContent));  // This will log true if it meets the conditions, otherwise false.


const RichTextEditorComponent: React.FC<RichTextEditorComponentProps> = (props) => {
    const {value: propsValue, onChange, readOnly, errorMessage, label, required} = props;
    const renderElement = useCallback((props: any) => <Element {...props} />, []);
    const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);
    const editor = useMemo(() => withReact(createEditor()), []);
    const [showError, setShowError] = useState(false);

    const handleKeyDown = (event: any) => {
        for (const hotkey in HOTKEYS) {
            if (isHotkey(hotkey, event)) {
                event.preventDefault();
                const mark = HOTKEYS[hotkey];
                toggleMark(editor, mark);
            }
        }
    };
    return (
        <div className={`rich-text-editor-component ${errorMessage && showError ? "has-error" : ""}`}>
            {label && <FormHelperText>
                <span className={"rich-text-helper"}>{label}</span>
                {required && <span className="required-asterisk">*</span>}
            </FormHelperText>}
            <Slate editor={editor} initialValue={reverseTransformer(propsValue)}
                   onChange={(newValue) => {
                       onChange && onChange(htmlTransformer(newValue));
                       // onChange && isMaximumLineCountReached(htmlTransformer(newValue));
                   }}>
                {
                    !readOnly && <div className="toolbar-action">
                        <MarkButton format="bold" icon="bold"/>
                        {/*<MarkButton format="italic" icon="italic"/>*/}
                        <MarkButton format="underline" icon="underline"/>
                        <BlockButton format="numbered-list" icon="orderedList"/>
                        <BlockButton format="bulleted-list" icon="unorderedList"/>
                    </div>
                }
                <Editable
                    onBlur={(e) => {
                        setShowError(true);
                    }}
                    className="rich-text-editor-box"
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    readOnly={readOnly}
                    placeholder={!readOnly ? "Enter Text" : "No content available"}
                    spellCheck
                    // autoFocus
                    onKeyDown={handleKeyDown}
                />
            </Slate>
            {/*<ButtonComponent handleClick={() => console.log(htmlTransformer(value))}>Save</ButtonComponent>*/}
            <FormHelperText>
                {errorMessage && showError && <p className={"rich-text-error rich-text-helper"}>{errorMessage}</p>}
            </FormHelperText>
        </div>
    );
};

export default RichTextEditorComponent;
